import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { forgetGame, joinGame, storedKey } from './join';
import { useLocation, useNavigate } from 'react-router';
import { isCancelledSignIn, onAuthChange, signInWithGoogle, signOut } from '../../auth';
import GuestHome from './GuestHome';
import SignIn from './SignIn';
import SignedInHome from './SignedInHome';
import Account from './Account';
import MyDecks from './MyDecks';
import DeckEditor from './DeckEditor';
import { forgetPick, initialPick, intersect, pickable, readPick } from './decks';

const SIGNED_OUT_SCREENS = ['guest', 'signin', 'notInvited'];
const SIGNED_IN_SCREENS = ['host', 'join', 'account', 'decks', 'deck'];

function Home({ onUserLoggedIn, onReady = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [account, setAccount] = useState(null);
  const [signInFailed, setSignInFailed] = useState(false);
  const [code, setCode] = useState(sessionStorage.getItem("gameCode") ?? "");
  const [nickname, setNickname] = useState(localStorage.getItem("userName") ?? "");
  const [joinError, setJoinError] = useState(null); // 'notFound' | 'nameTaken' | 'general'
  const [blockedUntil, setBlockedUntil] = useState(null); // too many wrong codes: join is locked until then
  const blockTimer = useRef(null);
  // A reload with a stored seat key goes straight back into the game.
  const [rejoining, setRejoining] = useState(() => code !== '' && storedKey(code, nickname) !== null);
  const signingIn = useRef(false);
  const joining = useRef(false);
  const session = useRef(0); // bumped on sign-out so late results of the old session are dropped
  // The account's deck list (GET /decks) and the in-session pick; kept here so they survive my decks and back.
  const [decks, setDecks] = useState(null);
  const [minAnswerCards, setMinAnswerCards] = useState(null);
  const [pick, setPick] = useState(null); // { selected: {question, answer}, dropped: {question, answer} }
  const [decksFailed, setDecksFailed] = useState(false);

  // Each screen is a history entry; `depth` tells whether Back stays inside this app.
  const depth = location.state?.depth ?? 0;
  const go = (screen, replace = false, extra = {}) => {
    setJoinError(null);
    navigate(location.pathname, { replace, state: { ...extra, screen, depth: replace ? depth : depth + 1 } });
  };
  const back = (fallback, extra) => (depth > 0 ? navigate(-1) : go(fallback, true, extra));

  const clearDecks = () => {
    setDecks(null);
    setMinAnswerCards(null);
    setPick(null);
    setDecksFailed(false);
  };

  const dropSession = async () => {
    session.current += 1;
    setAccount(null);
    clearDecks();
    try { await signOut(); } catch (e) { /* already signed out locally */ }
  };

  // 401 → guest home; 403 not_invited → not-invited screen. Returns whether it handled the failure.
  const onAuthFailure = async error => {
    const status = error?.response?.status;
    if (status === 401) {
      go('guest', true);
      await dropSession();
      return true;
    }
    if (status === 403 && error.response.data?.detail === 'not_invited') {
      go('notInvited', true);
      await dropSession();
      return true;
    }
    return false;
  };

  const loadMe = async () => {
    try {
      const res = await axios.get('me');
      setAccount(res.data);
      return true;
    } catch (e) {
      if (!(await onAuthFailure(e))) {
        setSignInFailed(true);
        go('signin', true);
      }
      return false;
    }
  };

  useEffect(() => {
    let first = true;
    return onAuthChange(user => {
      if (first) {
        first = false;
        // Session restore: an existing Firebase user still needs its account from the server.
        if (user) loadMe().finally(() => setReady(true));
        else setReady(true);
      } else if (!user) {
        session.current += 1;
        setAccount(null);
        clearDecks();
      }
      // A later signed-in event comes from the popup; handleGoogle loads the account itself.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every GET /decks result trims the pick to the decks still listed; the first one of a session starts it.
  // A failed load leaves the pick alone.
  const loadDecks = async () => {
    const mySession = session.current;
    try {
      const res = await axios.get('decks');
      if (session.current !== mySession) return;
      const list = res.data.decks;
      setDecks(list);
      setMinAnswerCards(res.data.min_answer_cards);
      setDecksFailed(false);
      setPick(p => (p === null
        ? initialPick(list, readPick(account.uid))
        : { ...p, selected: intersect(p.selected, list) }));
    } catch (e) {
      if (session.current !== mySession || await onAuthFailure(e)) return;
      setDecksFailed(true);
    }
  };

  const toggleDeck = (type, id) => setPick(p => {
    if (p === null) return p;
    const on = p.selected[type].includes(id) ? p.selected[type].filter(x => x !== id) : [...p.selected[type], id];
    const order = pickable(decks, type).map(d => d.id);
    return { ...p, selected: { ...p.selected, [type]: order.filter(x => on.includes(x)) } };
  });

  const handleGoogle = async () => {
    if (signingIn.current) return;
    signingIn.current = true;
    setSignInFailed(false);
    try {
      await signInWithGoogle();
    } catch (e) {
      if (!isCancelledSignIn(e)) {
        setSignInFailed(true);
        go('signin', true);
      }
      signingIn.current = false;
      return;
    }
    if (await loadMe()) go('host', true);
    signingIn.current = false;
  };

  useEffect(() => () => clearTimeout(blockTimer.current), []);

  const block = retryAfter => {
    const seconds = parseInt(retryAfter, 10) > 0 ? parseInt(retryAfter, 10) : 60;
    clearTimeout(blockTimer.current);
    setBlockedUntil(Date.now() + seconds * 1000);
    blockTimer.current = setTimeout(() => setBlockedUntil(null), seconds * 1000);
  };

  const join = async () => {
    if (joining.current || blockedUntil !== null) return;
    joining.current = true;
    setJoinError(null);
    try {
      const res = await joinGame(code, nickname);
      onUserLoggedIn(code, res.data.player, res.data.player.name);
    } catch (e) {
      if (await onAuthFailure(e)) return;
      const status = e.response?.status;
      if (status === 429) block(e.response.headers?.['retry-after']);
      else setJoinError(status === 404 ? 'notFound' : status === 409 ? 'nameTaken' : 'general');
    } finally {
      joining.current = false;
    }
  };

  useEffect(() => {
    if (!ready || !rejoining) return;
    joinGame(code, nickname)
      .then(res => onUserLoggedIn(code, res.data.player, res.data.player.name))
      .catch(e => {
        // Only a gone game (404) or a dead key (409) forgets the seat; a short outage must not cost it.
        if ([404, 409].includes(e.response?.status)) {
          forgetGame(code, nickname);
          try { sessionStorage.removeItem('gameCode'); } catch (err) { /* nothing to clear */ }
        }
        setRejoining(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // The splash stays up until Home has a screen to show.
  useEffect(() => {
    if (ready && !rejoining) onReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, rejoining]);

  const onCode = value => {
    setCode(value);
    if (joinError === 'notFound') setJoinError(null);
  };

  const onNickname = value => {
    setNickname(value);
    if (joinError === 'nameTaken') setJoinError(null);
  };

  if (!ready || rejoining) return null;

  const requested = location.state?.screen;
  const screen = account
    ? (SIGNED_IN_SCREENS.includes(requested) ? requested : 'host')
    : (SIGNED_OUT_SCREENS.includes(requested) ? requested : 'guest');
  const joinProps = {
    code, onCode, nickname, onNickname, join: { run: join, error: joinError, blocked: blockedUntil !== null },
  };

  switch (screen) {
    case 'signin':
    case 'notInvited':
      return <SignIn notInvited={screen === 'notInvited'} error={screen === 'signin' && signInFailed}
        onGoogle={handleGoogle} onJoinWithCode={() => back('guest')} />;
    case 'host':
    case 'join':
      return <SignedInHome {...joinProps} account={account} tab={screen} onTab={tab => go(tab)}
        onEnterGame={onUserLoggedIn} onAuthFailure={onAuthFailure} session={session}
        decks={decks} minAnswerCards={minAnswerCards} pick={pick} decksFailed={decksFailed}
        onLoadDecks={loadDecks} onToggleDeck={toggleDeck} onOpenDecks={tab => go('decks', false, { tab })}
        onOpenAccount={() => go('account')}
        onSignOut={async () => { go('guest', true); await dropSession(); }} />;
    case 'decks': {
      const tab = location.state?.tab === 'question' ? 'question' : 'answer';
      return <MyDecks tab={tab} decks={decks} loadFailed={decksFailed} onLoad={loadDecks} onAuthFailure={onAuthFailure}
        onTab={next => go('decks', true, { tab: next })} onBack={() => back('host')}
        onOpenDeck={deckId => go('deck', false, { deckId, tab })} />;
    }
    case 'deck': {
      const tab = location.state?.tab === 'question' ? 'question' : 'answer';
      const deckId = String(location.state?.deckId ?? '');
      // The open share sheet is its own history entry, so browser Back closes it; `key` keeps the editor mounted.
      return <DeckEditor key={deckId} deckId={deckId} tab={tab} onAuthFailure={onAuthFailure}
        sharing={location.state?.share === true}
        onShare={() => go('deck', false, { deckId, tab, share: true })}
        onCloseShare={() => back('deck', { deckId, tab })}
        onBack={() => back('decks', { tab })} onGone={() => go('decks', true, { tab })} />;
    }
    case 'account':
      return <Account account={account} onBack={() => back('host')} onAuthFailure={onAuthFailure}
        onSaved={saved => { setAccount(saved); back('host'); }}
        onDeleted={async () => { forgetPick(account.uid); setSignInFailed(false); go('signin', true); await dropSession(); }} />;
    default:
      return <GuestHome {...joinProps} onHost={() => { setSignInFailed(false); go('signin'); }} />;
  }
}

Home.propTypes = {
  onUserLoggedIn: PropTypes.func.isRequired,
  onReady: PropTypes.func,
};

export default Home;
